import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { AIInput } from '@/components/ui/ai-input';
import { AITextarea } from '@/components/ui/ai-textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  MarketingActivity,
  ActivityChecklist,
  ActivityComment,
  ActivityType,
  ActivityStatus,
  PriorityLevel,
  Campaign,
  MarketingPhase,
  User,
  Team,
} from '@/types';
import type { ApiResponse } from '@/types';
import { cn, formatCurrency, formatDate, statusColors, priorityColors, getInitials } from '@/lib/utils';
import { api } from '@/lib/api';
import { X, Pencil, Plus, Upload, MessageSquare, Download, Loader2, Save, Send, Target, Users } from 'lucide-react';
import { useStrategyStore } from '@/stores/strategy';

interface ActivityDetailDrawerProps {
  activityId: string | null;
  open: boolean;
  onClose: () => void;
  onUpdated?: () => void;
}

export default function ActivityDetailDrawer({
  activityId,
  open,
  onClose,
  onUpdated,
}: ActivityDetailDrawerProps) {
  const [activity, setActivity] = useState<MarketingActivity | null>(null);
  const [loading, setLoading] = useState(false);
  const [newChecklistTitle, setNewChecklistTitle] = useState('');
  const [newComment, setNewComment] = useState('');
  const [addingChecklist, setAddingChecklist] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [includeChecklistsInPdf, setIncludeChecklistsInPdf] = useState(true);
  const [includeCommentsInPdf, setIncludeCommentsInPdf] = useState(true);
  const [pdfDefaultsLoaded, setPdfDefaultsLoaded] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    type: 'other' as ActivityType,
    status: 'backlog' as ActivityStatus,
    priority: 'medium' as PriorityLevel,
    start_date: '',
    end_date: '',
    budget_planned: '0',
    hours_planned: '0',
    campaign_id: null as string | null,
    phase_id: null as string | null,
    assigned_to: null as string | null,
    team_id: null as string | null,
  });
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [phases, setPhases] = useState<MarketingPhase[]>([]);
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [teamOptions, setTeamOptions] = useState<Team[]>([]);
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [assigningChecklistItem, setAssigningChecklistItem] = useState<string | null>(null);

  const activityLoadRequest = useRef(0);
  const loadActivity = useCallback(async () => {
    const request = ++activityLoadRequest.current;
    if (!activityId) {
      setActivity(null);
      return;
    }
    setLoading(true);
    setPhases([]);
    try {
      const [actRes, usersRes] = await Promise.all([
        api.get<ApiResponse<MarketingActivity>>(`/activities/${activityId}`),
        api.get<ApiResponse<User[]>>('/users?limit=100&is_active=true').catch(() => ({ data: [] })),
      ]);
      if (request !== activityLoadRequest.current) return;
      const loaded = actRes.data ?? null;
      setActivity(loaded);
      setUserOptions(Array.isArray(usersRes.data) ? usersRes.data : []);
      if (loaded?.plan_id) {
        void api
          .get<ApiResponse<{ phases?: MarketingPhase[] }>>(`/marketing-plans/${loaded.plan_id}`)
          .then((planRes) => { if (request === activityLoadRequest.current) setPhases(Array.isArray(planRes.data?.phases) ? planRes.data.phases : []); })
          .catch(() => { if (request === activityLoadRequest.current) setPhases([]); });
      }
    } catch {
      if (request === activityLoadRequest.current) setActivity(null);
    } finally {
      if (request === activityLoadRequest.current) setLoading(false);
    }
  }, [activityId]);

  useEffect(() => {
    if (open && activityId) void loadActivity();
    return () => { activityLoadRequest.current += 1; };
  }, [open, activityId, loadActivity]);

  useEffect(() => {
    if (!open || pdfDefaultsLoaded) return;
    void api
      .get<ApiResponse<{ include_checklists_default: boolean; include_comments_default: boolean }>>('/export/pdf-settings')
      .then((res) => {
        if (typeof res.data?.include_checklists_default === 'boolean') {
          setIncludeChecklistsInPdf(res.data.include_checklists_default);
        }
        if (typeof res.data?.include_comments_default === 'boolean') {
          setIncludeCommentsInPdf(res.data.include_comments_default);
        }
      })
      .catch(() => {
        // Keep current defaults if settings endpoint is unavailable.
      })
      .finally(() => setPdfDefaultsLoaded(true));
  }, [open, pdfDefaultsLoaded]);

  const handleChecklistToggle = async (item: ActivityChecklist) => {
    if (!activity) return;
    try {
      await api.patch<ApiResponse<ActivityChecklist>>(
        `/activities/${activity.id}/checklists/${item.id}`,
        { is_completed: !item.is_completed }
      );
      loadActivity();
      onUpdated?.();
    } catch {
      // ignore
    }
  };

  const handleChecklistAssign = async (item: ActivityChecklist, userId: string | null) => {
    if (!activity) return;
    setAssigningChecklistItem(item.id);
    try {
      await api.patch<ApiResponse<ActivityChecklist>>(
        `/activities/${activity.id}/checklists/${item.id}`,
        { assigned_to: userId }
      );
      loadActivity();
    } catch {
      // ignore
    } finally {
      setAssigningChecklistItem(null);
    }
  };

  const handleAddChecklistItem = async () => {
    if (!activity || !newChecklistTitle.trim()) return;
    setAddingChecklist(true);
    try {
      await api.post<ApiResponse<ActivityChecklist>>(
        `/activities/${activity.id}/checklists`,
        { title: newChecklistTitle.trim(), sort_order: (activity.checklists?.length ?? 0) }
      );
      setNewChecklistTitle('');
      loadActivity();
      onUpdated?.();
    } finally {
      setAddingChecklist(false);
    }
  };

  const handleAddComment = async () => {
    if (!activity || !newComment.trim()) return;
    setAddingComment(true);
    try {
      await api.post<ApiResponse<ActivityComment>>(
        `/activities/${activity.id}/comments`,
        { content: newComment.trim() }
      );
      setNewComment('');
      loadActivity();
      onUpdated?.();
    } finally {
      setAddingComment(false);
    }
  };

  const handleExportPdf = async () => {
    if (!activity) return;
    setExportError(null);
    setExportingPdf(true);
    try {
      const params = new URLSearchParams({
        include_checklists: includeChecklistsInPdf ? '1' : '0',
        include_comments: includeCommentsInPdf ? '1' : '0',
      });
      const blob = await api.getBlob(`/export/activity/${activity.id}/pdf?${params.toString()}`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-${activity.id}-detail.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'PDF Export fehlgeschlagen.');
    } finally {
      setExportingPdf(false);
    }
  };

  const openEditDialog = () => {
    if (!activity) return;
    setEditError(null);
    setEditForm({
      title: activity.title,
      description: activity.description ?? activity.description_md ?? '',
      type: activity.type,
      status: activity.status,
      priority: activity.priority,
      start_date: activity.start_date ?? '',
      end_date: activity.end_date ?? '',
      budget_planned: String(activity.budget_planned ?? 0),
      hours_planned: String(activity.hours_planned ?? 0),
      campaign_id: activity.campaign_id ?? null,
      phase_id: activity.phase_id ?? null,
      assigned_to: activity.assigned_to ?? null,
      team_id: activity.team_id ?? null,
    });
    void api
      .get<ApiResponse<Campaign[]>>(`/campaigns?plan_id=${activity.plan_id}&limit=200`)
      .then((res) => setCampaigns(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCampaigns([]));
    void api
      .get<ApiResponse<{ phases?: MarketingPhase[] }>>(`/marketing-plans/${activity.plan_id}`)
      .then((res) => setPhases(Array.isArray(res.data?.phases) ? res.data.phases : []))
      .catch(() => setPhases([]));
    void api
      .get<ApiResponse<User[]>>('/users?limit=200&is_active=true')
      .then((res) => setUserOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setUserOptions([]));
    void api
      .get<ApiResponse<Team[]>>('/teams?limit=200')
      .then((res) => setTeamOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTeamOptions([]));
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!activity) return;
    setEditError(null);
    if (!editForm.title.trim()) {
      setEditError('Bitte einen Titel eingeben.');
      return;
    }
    if (editForm.end_date && editForm.start_date && editForm.end_date < editForm.start_date) {
      setEditError('Das Enddatum muss nach dem Startdatum liegen.');
      return;
    }
    const budgetPlanned = Number(editForm.budget_planned);
    const hoursPlanned = Number(editForm.hours_planned);
    if (Number.isNaN(budgetPlanned) || budgetPlanned < 0 || Number.isNaN(hoursPlanned) || hoursPlanned < 0) {
      setEditError('Budget und Stunden müssen gültige positive Werte sein.');
      return;
    }
    setSaving(true);
    try {
      await api.patch<ApiResponse<MarketingActivity>>(`/activities/${activity.id}`, {
        title: editForm.title.trim(),
        description: editForm.description.trim() || null,
        description_md: editForm.description.trim() || null,
        type: editForm.type,
        status: editForm.status,
        priority: editForm.priority,
        start_date: editForm.start_date || null,
        end_date: editForm.end_date || null,
        budget_planned: budgetPlanned,
        hours_planned: hoursPlanned,
        campaign_id: editForm.campaign_id || null,
        phase_id: editForm.phase_id || null,
        assigned_to: editForm.assigned_to || null,
        team_id: editForm.team_id || null,
      });
      setEditDialogOpen(false);
      await loadActivity();
      onUpdated?.();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Aktivität konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const content = (
    <>
      <div
        className="fixed inset-0 z-40 bg-foreground/50 transition-opacity"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[600px] flex-col border-l border-border bg-card shadow-xl dark:bg-background transition-transform duration-300 ease-out translate-x-0"
        role="dialog"
        aria-label="Aktivitätsdetails"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">Aktivitätsdetails</h2>
            <StrategyChip />
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <ScrollArea className="flex-1">
          <div className="space-y-6 p-6">
            {loading ? (
              <div className="space-y-4">
                <div className="h-8 w-3/4 animate-pulse rounded bg-card-hover" />
                <div className="h-4 w-1/2 animate-pulse rounded bg-card-hover" />
                <div className="h-24 animate-pulse rounded bg-card-hover" />
              </div>
            ) : !activity ? (
              <p className="text-muted">Aktivität nicht gefunden.</p>
            ) : (
              <>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <h3 className="text-xl font-bold">{activity.title}</h3>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleExportPdf} disabled={exportingPdf}>
                      {exportingPdf ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                      PDF exportieren
                    </Button>
                    <Button variant="outline" size="sm" onClick={openEditDialog}>
                      <Pencil className="h-4 w-4" />
                      Bearbeiten
                    </Button>
                  </div>
                </div>
                <div className="rounded-md border border-border bg-background p-3 dark:bg-card/40">
                  <p className="mb-2 text-xs font-medium text-muted">PDF Optionen</p>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={includeChecklistsInPdf}
                        onCheckedChange={(checked) => setIncludeChecklistsInPdf(Boolean(checked))}
                      />
                      Checkliste einbeziehen
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={includeCommentsInPdf}
                        onCheckedChange={(checked) => setIncludeCommentsInPdf(Boolean(checked))}
                      />
                      Kommentare einbeziehen
                    </label>
                  </div>
                </div>
                {exportError && (
                  <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                    {exportError}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Badge className={cn(statusColors[activity.status])}>
                    {activity.status}
                  </Badge>
                  <Badge className={cn(priorityColors[activity.priority])}>
                    {activity.priority}
                  </Badge>
                  <Badge variant="secondary">{activity.type}</Badge>
                </div>

                {(activity.description ?? activity.description_md) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Beschreibung</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="prose prose-slate dark:prose-invert max-w-none whitespace-pre-wrap text-sm">
                        {activity.description ?? activity.description_md}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Users className="h-4 w-4" />
                      Aufgabenverteilung
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9 shrink-0">
                        <AvatarFallback className="text-xs font-medium">
                          {activity.assignee ? getInitials(activity.assignee.full_name) : '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-muted">Verantwortliche Person</p>
                        <p className="font-medium text-sm truncate">
                          {activity.assignee?.full_name ?? (
                            <span className="text-muted italic">Nicht zugewiesen</span>
                          )}
                        </p>
                        {activity.assignee?.email && (
                          <p className="text-xs text-muted truncate">{activity.assignee.email}</p>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" onClick={openEditDialog} className="shrink-0 text-xs">
                        <Pencil className="h-3 w-3" />
                        Ändern
                      </Button>
                    </div>
                    {activity.team && (
                      <div className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2 dark:bg-card/40">
                        <div
                          className="h-3 w-3 shrink-0 rounded-full"
                          style={{ backgroundColor: activity.team.color ?? '#94a3b8' }}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-muted">Team</p>
                          <p className="font-medium text-sm truncate">{activity.team.name}</p>
                        </div>
                      </div>
                    )}
                    {!activity.team && !activity.assignee && (
                      <p className="text-xs text-muted italic">
                        Noch keine Zuweisung. Klicke auf „Bearbeiten", um eine Person oder ein Team zuzuweisen.
                      </p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {activity.phase_id && (
                      <p>
                        <span className="text-muted">Phase / Quartal:</span>{' '}
                        <span className="font-medium">
                          {phases.find((p) => p.id === activity.phase_id)?.name ?? activity.phase_id}
                        </span>
                      </p>
                    )}
                    {activity.campaign_id && (
                      <p>
                        <span className="text-muted">Kampagne:</span>{' '}
                        <span className="font-medium text-accent">
                          {campaigns.find((c) => c.id === activity.campaign_id)?.name ?? activity.campaign_id}
                        </span>
                      </p>
                    )}
                    <p>
                      <span className="text-muted">Lieferant:</span>{' '}
                      {activity.supplier?.name ?? '—'}
                    </p>
                    <p>
                      <span className="text-muted">Zeitraum:</span>{' '}
                      {activity.start_date
                        ? formatDate(activity.start_date)
                        : '—'}{' '}
                      –{' '}
                      {activity.end_date
                        ? formatDate(activity.end_date)
                        : '—'}
                    </p>
                    <p>
                      <span className="text-muted">Budget (geplant):</span>{' '}
                      {formatCurrency(activity.budget_planned)}
                    </p>
                    <p>
                      <span className="text-muted">Budget (tatsächlich):</span>{' '}
                      {formatCurrency(activity.budget_actual)}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Aufgaben / Checkliste</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {activity.checklists?.length ? (
                      <ul className="space-y-2">
                        {activity.checklists
                          .slice()
                          .sort((a, b) => a.sort_order - b.sort_order)
                          .map((item) => (
                            <li
                              key={item.id}
                              className="flex items-start gap-2 rounded-lg border border-border p-2"
                            >
                              <Checkbox
                                className="mt-0.5 shrink-0"
                                checked={item.is_completed}
                                onCheckedChange={() =>
                                  handleChecklistToggle(item)
                                }
                              />
                              <div className="min-w-0 flex-1">
                                <span
                                  className={cn(
                                    'text-sm',
                                    item.is_completed && 'text-muted line-through'
                                  )}
                                >
                                  {item.title}
                                </span>
                                <div className="mt-1 flex items-center gap-2">
                                  {item.assignee ? (
                                    <div className="flex items-center gap-1.5">
                                      <Avatar className="h-5 w-5">
                                        <AvatarFallback className="text-[10px]">
                                          {getInitials(item.assignee.full_name)}
                                        </AvatarFallback>
                                      </Avatar>
                                      <span className="text-xs text-muted">{item.assignee.full_name}</span>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted italic">Nicht zugewiesen</span>
                                  )}
                                  {userOptions.length > 0 && (
                                    <Select
                                      value={item.assigned_to ?? 'none'}
                                      onValueChange={(v) =>
                                        handleChecklistAssign(item, v === 'none' ? null : v)
                                      }
                                      disabled={assigningChecklistItem === item.id}
                                    >
                                      <SelectTrigger className="h-6 w-auto min-w-[90px] border-dashed px-2 text-xs">
                                        <SelectValue placeholder="Zuweisen" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">Niemand</SelectItem>
                                        {userOptions.map((u) => (
                                          <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  )}
                                </div>
                              </div>
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted">
                        Keine Aufgaben. Füge unten eine hinzu.
                      </p>
                    )}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Neue Aufgabe…"
                        value={newChecklistTitle}
                        onChange={(e) =>
                          setNewChecklistTitle(e.target.value)
                        }
                        onKeyDown={(e) =>
                          e.key === 'Enter' && handleAddChecklistItem()
                        }
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        disabled={!newChecklistTitle.trim() || addingChecklist}
                        onClick={handleAddChecklistItem}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <MessageSquare className="h-4 w-4" />
                      Kommentare
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {activity.comments?.length ? (
                      <ul className="space-y-3">
                        {activity.comments.map((c) => (
                          <li
                            key={c.id}
                            className="flex gap-3 rounded-lg border border-border p-3"
                          >
                            <Avatar className="h-8 w-8 shrink-0">
                              <AvatarFallback className="text-xs">
                                {c.user
                                  ? getInitials(c.user.full_name)
                                  : '?'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium">
                                {c.user?.full_name ?? 'Unbekannt'}
                              </p>
                              <p className="text-xs text-muted">
                                {formatDate(c.created_at, 'long')}
                              </p>
                              <p className="mt-1 whitespace-pre-wrap text-sm">
                                {c.content}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted">
                        Noch keine Kommentare.
                      </p>
                    )}
                    <div className="space-y-2">
                      <AITextarea
                        placeholder="Kommentar hinzufügen…"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onValueChange={setNewComment}
                        rows={2}
                        aiFieldLabel="Aktivitäts-Kommentar"
                        aiContext={`Kommentar zur Aktivität: ${activity.title}`}
                      />
                      <Button
                        size="sm"
                        disabled={!newComment.trim() || addingComment}
                        onClick={handleAddComment}
                      >
                        <Send className="h-4 w-4" />
                        Kommentar senden
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Dokumente</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-sm text-muted">
                      Angehängte Dokumente (Upload kann an API angebunden werden).
                    </p>
                    <Button variant="outline" size="sm">
                      <Upload className="h-4 w-4" />
                      Hochladen
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Erlöse / KPIs</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted">Leads (erwartet / tatsächlich)</p>
                      <p>
                        {activity.expected_leads} / {activity.actual_leads}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted">Conversions (erwartet / tatsächlich)</p>
                      <p>
                        {activity.expected_conversions} /{' '}
                        {activity.actual_conversions}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted">Umsatz (erwartet)</p>
                      <p>{formatCurrency(activity.expected_revenue)}</p>
                    </div>
                    <div>
                      <p className="text-muted">Umsatz (tatsächlich)</p>
                      <p>{formatCurrency(activity.actual_revenue)}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Verlauf</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted">
                      Änderungsverlauf (Timeline) kann hier angezeigt werden, sobald die API
                      Aktivitäts-Historie liefert.
                    </p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    </>
  );

  return (
    <>
      {createPortal(content, document.body)}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Aktivität bearbeiten</DialogTitle>
            <DialogDescription>
              Felder anpassen und speichern.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {editError && (
              <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                {editError}
              </p>
            )}
            <div className="space-y-1">
              <Label htmlFor="edit-title">Titel *</Label>
              <AIInput
                id="edit-title"
                value={editForm.title}
                onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                onValueChange={(v) => setEditForm((f) => ({ ...f, title: v }))}
                placeholder="Titel der Aktivität"
                aiFieldLabel="Aktivitäts-Titel"
                aiContext={`Marketing-Aktivität, Typ: ${activity?.type ?? ''}`}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-description">Beschreibung</Label>
              <AITextarea
                id="edit-description"
                value={editForm.description}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                onValueChange={(v) => setEditForm((f) => ({ ...f, description: v }))}
                placeholder="Optionale Beschreibung"
                rows={3}
                aiFieldLabel="Aktivitäts-Beschreibung"
                aiContext={`Aktivität: ${(editForm.title || activity?.title) ?? ''}, Typ: ${activity?.type ?? ''}`}
              />
            </div>
            <div className="space-y-1">
              <Label>Phase / Quartal</Label>
              <Select
                value={editForm.phase_id ?? 'none'}
                onValueChange={(v) => setEditForm((f) => ({ ...f, phase_id: v === 'none' ? null : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Keine Phase" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Phase</SelectItem>
                  {phases.map((phase) => (
                    <SelectItem key={phase.id} value={phase.id}>
                      {phase.name}
                      {(phase.start_date || phase.end_date) && (
                        <span className="ml-1 text-xs text-muted">
                          ({[phase.start_date, phase.end_date].filter(Boolean).join(' – ')})
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Kampagne</Label>
              <Select
                value={editForm.campaign_id ?? 'none'}
                onValueChange={(v) => setEditForm((f) => ({ ...f, campaign_id: v === 'none' ? null : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Keine Kampagne" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Kampagne</SelectItem>
                  {campaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(userOptions.length > 0 || teamOptions.length > 0) && (
              <div className="grid grid-cols-2 gap-3">
                {userOptions.length > 0 && (
                  <div className="space-y-1">
                    <Label>Zugewiesen an</Label>
                    <Select
                      value={editForm.assigned_to ?? 'none'}
                      onValueChange={(v) => setEditForm((f) => ({ ...f, assigned_to: v === 'none' ? null : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Niemand" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Niemand</SelectItem>
                        {userOptions.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {teamOptions.length > 0 && (
                  <div className="space-y-1">
                    <Label>Team</Label>
                    <Select
                      value={editForm.team_id ?? 'none'}
                      onValueChange={(v) => setEditForm((f) => ({ ...f, team_id: v === 'none' ? null : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Kein Team" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Kein Team</SelectItem>
                        {teamOptions.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Typ</Label>
                <Select
                  value={editForm.type}
                  onValueChange={(v) => setEditForm((f) => ({ ...f, type: v as ActivityType }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="google_ads">Google Ads</SelectItem>
                    <SelectItem value="linkedin_ads">LinkedIn Ads</SelectItem>
                    <SelectItem value="print">Print</SelectItem>
                    <SelectItem value="email">E-Mail</SelectItem>
                    <SelectItem value="campaign">Kampagne</SelectItem>
                    <SelectItem value="social">Social</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="content">Content</SelectItem>
                    <SelectItem value="seo">SEO</SelectItem>
                    <SelectItem value="webinar">Webinar</SelectItem>
                    <SelectItem value="other">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(v) => setEditForm((f) => ({ ...f, status: v as ActivityStatus }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="backlog">Backlog</SelectItem>
                    <SelectItem value="todo">Todo</SelectItem>
                    <SelectItem value="in_progress">In Bearbeitung</SelectItem>
                    <SelectItem value="review">Review</SelectItem>
                    <SelectItem value="done">Erledigt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Priorität</Label>
                <Select
                  value={editForm.priority}
                  onValueChange={(v) => setEditForm((f) => ({ ...f, priority: v as PriorityLevel }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Niedrig</SelectItem>
                    <SelectItem value="medium">Mittel</SelectItem>
                    <SelectItem value="high">Hoch</SelectItem>
                    <SelectItem value="critical">Kritisch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="edit-start">Startdatum</Label>
                <Input
                  id="edit-start"
                  type="date"
                  value={editForm.start_date}
                  onChange={(e) => setEditForm((f) => ({ ...f, start_date: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-end">Enddatum</Label>
                <Input
                  id="edit-end"
                  type="date"
                  value={editForm.end_date}
                  onChange={(e) => setEditForm((f) => ({ ...f, end_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="edit-budget">Budget geplant (CHF)</Label>
                <Input
                  id="edit-budget"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.budget_planned}
                  onChange={(e) => setEditForm((f) => ({ ...f, budget_planned: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-hours">Stunden geplant</Label>
                <Input
                  id="edit-hours"
                  type="number"
                  min="0"
                  step="0.5"
                  value={editForm.hours_planned}
                  onChange={(e) => setEditForm((f) => ({ ...f, hours_planned: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
              <X className="h-4 w-4" />
              Abbrechen
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Speichern…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Speichern
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StrategyChip() {
  const { strategy, openPanel } = useStrategyStore();
  if (!strategy) return null;
  return (
    <button
      type="button"
      onClick={() => void openPanel(strategy.year)}
      className="flex items-center gap-1 rounded-md bg-card-hover px-2 py-0.5 text-xs font-medium text-foreground hover:bg-border dark:bg-primary-hover/30 dark:text-muted dark:hover:bg-primary-hover/50"
    >
      <Target size={11} />
      Strategie {strategy.year}
    </button>
  );
}
